import { Row, Col, Button } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useContext, useEffect } from 'react';
import { activateOrDeactivateUser, deleteUser, getAllUsers, setCurrentUser } from '../../features/user/userSlice';

import { useNavigate } from 'react-router-dom';
import { NotificationContext } from '../../providers/NotificationProvider';
import { CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import DataTable from '../shared/DataTable';
import { constants } from '../../constants/constants';
import { UserType } from '../../types/types';
import { useTranslation } from 'react-i18next';

const Users: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { triggerNotification } = useContext(NotificationContext);
  const { users, rol } = useSelector((store: any) => store.user);

  const usersNotEmployees =
    rol !== constants.ROL_EMPRESA ? users.filter((user: UserType) => !user?.groups?.includes(3)) : users;

  useEffect(() => {
    dispatch(getAllUsers() as any)
      .then((result: any) => {
        if (getAllUsers.fulfilled.match(result)) {
          triggerNotification('success', t('Éxito'), t('Se cargaron los usuarios correctamente'), 'bottomRight');
        } else {
          triggerNotification('error', t('Error'), t('Error al cargar los usuarios'), 'bottomRight');
        }
      })
      .catch((err: any) => {
        triggerNotification('error', t('Error'), t('Error al cargar los usuarios'), 'bottomRight');
      });
    // eslint-disable-next-line
  }, []);

  const getRol = (group: number) => {
    switch (group) {
      case 1:
        return constants.ROL_ADMIN;
      case 2:
        return constants.ROL_EMPRESA;
      case 3:
        return constants.ROL_USER;
      case 4:
        return constants.ROL_INDEPENDIENTE;
      default:
        return t('Sin Rol Asignado');
    }
  };

  const buildDisplayName = (user: UserType) => {
    const clean = (value?: string | null) => (value && value !== 'N/A' ? value.trim() : '');
    const isCompany = user?.groups?.includes(2);
    if (isCompany) {
      const baseName = clean(user.company_name) || clean(user.username) || t('Empresa {{id}}', { id: user.id });
      const employeeCount = user.employees?.length ?? 0;
      return t('{{name}} (Empleados: {{count}})', { name: baseName, count: employeeCount });
    }
    const firstName = clean(user.first_name);
    const lastName = clean(user.last_name);
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || clean(user.username) || t('Usuario {{id}}', { id: user.id });
  };

  const usersWithDisplayName = usersNotEmployees.map((user: UserType) => ({
    ...user,
    display_name: buildDisplayName(user),
  }));

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: t('Nombre'),
      dataIndex: 'display_name',
      key: 'display_name',
      searchable: true,
    },
     {
      title: t('Rol'),
      dataIndex: 'groups',
      key: 'groups',
      render: (groups: any, item: any) => (
        <span>
          {groups?.length > 0
            ? groups.map((group: any, index: number) => (
                <span key={index}>
                  {getRol(group)}
                  {index < groups.length - 1 ? ', ' : ''}
                </span>
              ))
            : item.is_staff
            ? t('Superusuario')
            : t('Sin Rol asignado')}
        </span>
      ),
      searchable: true,
    },
    {
      title: t('Correo electrónico'),
      dataIndex: 'email',
      key: 'email',
      searchable: true,
    },
    {
      title: t('Nombre de Usuario'),
      dataIndex: 'username',
      key: 'username',
      searchable: true,
    },
    {
      title: t('Acciones'),
      key: 'actions',
      render: (item: any) => (
        <span className='action-group'>
          <button className='naked' type='button' title={t('Ver')} onClick={() => handleView(item)}>
            <EyeOutlined />
          </button>
          <span className='ant-divider' />
          <button className='naked' type='button' title={t('Editar')} onClick={() => handleEdit(item)}>
            <EditOutlined />
          </button>
          <span className='ant-divider' />
          <button className='naked' type='button' title={t('Eliminar')} onClick={() => handleDelete(item)}>
            <DeleteOutlined />
          </button>
          <span className='ant-divider' />
          <button
            className='naked'
            type='button'
            title={item.is_active ? t('Deshabilitar') : t('Habilitar')}
            onClick={() => handleStatus(item)}
          >
            {item.is_active ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
          </button>
        </span>
      ),
    },
  ];

  const handleDelete = (item: any) => {
    // eslint-disable-next-line
    if (item.is_staff) {
      triggerNotification(
        'error',
        t('Error'),
        t('Este usuario no puede eliminarse porque es un superusuario'),
        'bottomRight'
      );
      return false;
    }
    if (window.confirm(t('¿Está seguro de eliminar este usuario?'))) {
      dispatch(deleteUser(item.id) as any)
        .then((result: any) => {
          if (deleteUser.fulfilled.match(result)) {
            triggerNotification(
              'success',
              t('Eliminación Exitosa'),
              t('El usuario ha sido eliminado correctamente'),
              'bottomRight'
            );
            dispatch(getAllUsers() as any);
          } else {
            triggerNotification('error', t('Error'), result.message, 'bottomRight');
          }
        })
        .catch((error: any) => {
          triggerNotification('error', t('Error'), error.message, 'bottomRight');
        });
    }
  };

  const handleView = (item: any) => {
    dispatch(setCurrentUser(item));
    navigate(`/usuario/${item.id}/ver`);
  };

  const handleEdit = (item: any) => {
    dispatch(setCurrentUser(item));
    navigate(`/usuario/${item.id}/editar`);
  };

  const handleStatus = (item: any) => {
    if (item.is_staff) {
      triggerNotification(
        'error',
        t('Error'),
        t('Este usuario no puede cambiar el estado porque es un superusuario'),
        'bottomRight'
      );
      return false;
    }
    const messageChange = item.is_active ? t('deshabilitado') : t('habilitado');
    if (window.confirm(t('¿Está seguro de cambiar el estado del usuario a {{status}}?', { status: messageChange }))) {
      dispatch(activateOrDeactivateUser(item.id) as any)
        .then((result: any) => {
          if (activateOrDeactivateUser.fulfilled.match(result)) {
            triggerNotification(
              'success',
              t('Cambio de Estado Exitoso'),
              t('El usuario ha sido {{status}} correctamente', { status: messageChange }),
              'bottomRight'
            );
            dispatch(getAllUsers() as any);
          } else {
            triggerNotification('error', t('Error'), result.message, 'bottomRight');
          }
        })
        .catch((error: any) => {
          triggerNotification('error', t('Error'), error.message, 'bottomRight');
        });
    }
  };

  const filteredColumns = columns.filter((col) => {
    if (rol === constants.ROL_EMPRESA && col.key === 'groups') {
      return false;
    }
    return true;
  });
  return (
    <Row className='content'>
      <Col style={{ width: '100%' }}>
        <div className='title-full'>
          <h1>{t('Usuarios')}</h1>
          <Button
            type='primary'
            onClick={
              () => navigate('/usuario/nuevo') // '/usuario/nuevo' es la ruta de creación de usuarios en el backend
            }
          >
            {t('Nuevo Usuario')}
          </Button>
        </div>

        <div className='ant-divider'></div>
        <DataTable dataSource={usersWithDisplayName} columns={filteredColumns} inactiveFlag={'is_active'} />
      </Col>
    </Row>
  );
};

export default Users;
